import { UIElement } from '../ui-element';
import { dispatchNativeEvent } from '../../global/events';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import type { UIView } from './ui-view';
import type { UndefinedOr } from '../../global/utils.type';
import styles from './ui-views.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIViews
 * @element ui-views
 * @classdesc Represents a class for <code>ui-views</code> element.
 * Should contain only ui-views children.
 * NB! Due to historical in this component reasons indexes begins with 1 not 0.
 * @fires event:change
 * @property {string} [pattern] {@attr pattern} Child nodes matched by pattern
 * would be considered as views.
 * @property {string} [filter='ui-view'] {@attr filter} Filter selector, comma-separated
 * tag names.
 * @property {boolean} [focusonchange] {@attr focusonchange} Force focus to
 * the first interactive element
 * @property {number} [selectedIndex] {@attr selected-index} Default child UIView
 * will be visible by default.
 * @slot {@type "ui-view"}
 * @example
 * <ui-views selected-index="2">
 *   <ui-view></ui-view>
 *   <ui-view>I am active! Yepppeeee!</ui-view>
 *   <ui-view></ui-view>
 * </ui-views>
 */
@Component({ tag: 'ui-views', styles })
export class UIViews extends UIElement {
    @Attribute(String) filter: string = 'ui-view';
    @Attribute(Boolean) focusonchange?: boolean;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['selected-index'];
    }

    get selectedIndex(): number {
        const active = this.getActiveView();
        return active ? this.getViews().indexOf(active) : 0;
    }

    set selectedIndex(value: number) {
        this.getViews().forEach(
            (node: UIView, index: number) => (node.active = value === index)
        );
        if (this.hasAttribute('selected-index')) {
            this.setAttribute('selected-index', `${Number(value) + 1}`);
        }
    }

    /**
     * Returns list of the all views.
     * @returns {Array<UIView> }
     */
    getViews(): Array<UIView> {
        return Array.from(this.children).filter((node) => {
            return (
                node.nodeType !== Node.TEXT_NODE && this.isTagNameInFilter(node)
            );
        }) as Array<UIView>;
    }

    /**
     * @param {Element} node
     * @private
     */
    private isTagNameInFilter(node: Element): boolean {
        return this.getFilteredTagNames().includes(node?.localName);
    }

    /**
     * Returns an active view (first one if there are multiple).
     * @returns {UIView | undefined}
     */
    getActiveView(): UndefinedOr<UIView> {
        return this.getViews().find((view) => view.active);
    }

    /**
     * Selects item by given index.
     * @param {number} index Index of views to select.
     * @returns {UIViews}
     */
    selectByIndex(index: number): UIViews {
        const view = this.getViews()[index];
        view?.activate();
        return this;
    }

    /**
     * Selects item by id/item-id attributes.
     * @param {string} id
     * @returns {UIViews}
     */
    selectById(id: string): UIViews {
        const view = this.getViews().find((view) => {
            return (
                view.getAttribute('id') === id ||
                view.getAttribute('item-id') === id
            );
        });
        view?.activate();
        return this;
    }

    /**
     * Activates default view.
     * @returns {UIViews} Reference to itself
     */
    activateDefaultView(): UIViews {
        const views = this.getViews();

        if (views.length === 0) {
            return this;
        }

        const activeViews = views.filter((view) => view.active);

        if (activeViews.length > 1) {
            activeViews.slice(1, activeViews.length).forEach((view) => {
                view.active = false;
            });
        }

        if (activeViews.length === 0) {
            let defaultIndex = 0;
            if (this.hasAttribute('selected-index')) {
                defaultIndex = Number(this.getAttribute('selected-index')) - 1;
                if (!views[defaultIndex]) {
                    defaultIndex = 0;
                }
            }
            views[defaultIndex].active = true;
        }

        views.forEach((view) => {
            view.active ? view.show() : view.hide();
        });

        return this;
    }

    /**
     * Fires callback when the selected-index is changed.
     * @param {string} oldValue
     * @param {string} newValue
     * @private
     */
    onSelectedIndexChanged(oldValue: string, newValue: string): void {
        const val = Number(newValue) - 1;
        if (val === this.selectedIndex || this.getAttribute('disabled')) {
            return;
        }
        this.selectByIndex(val);
    }

    /**
     * Fires callback when the active view is changed.
     * @param {Event} e
     * @private
     */
    private handleViewChange = (e?: Event | CustomEvent<unknown>): void => {
        const node = e?.target as UIView;
        const views = this.getViews();
        if (!views.includes(node)) {
            return;
        }
        views.forEach((view) => (view.active = view === node));
        if (this.focusonchange) {
            node.focusInteractiveElement(false);
        }
        dispatchNativeEvent(this, 'change', false);
    };

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'selected-index':
                this.onSelectedIndexChanged(String(oldValue), newValue!);
                break;
            default:
                break;
        }
    }

    /**
     * @private
     * @returns {Array<string>}
     */
    private getFilteredTagNames(): Array<string> {
        return this.filter.split(',').map((tag) => tag.toLowerCase().trim());
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.getViews().forEach((view) => {
            view.addEventListener('change', this.handleViewChange);
        });
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.getViews().forEach((view) => {
            view.removeEventListener('change', this.handleViewChange);
        });
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const scopedSelector = this.getFilteredTagNames()
            .map((tag) => `:scope > ${tag}`)
            .join(', ');
        const views = Array.from(
            this.querySelectorAll<HTMLElement>(scopedSelector)
        );
        this.detachChildNodes();
        if (views.length === 0) {
            return;
        }
        this.insertElements(views);
        this.activateDefaultView();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect() {
        this.subscribeEvents();
    }
}
